"use client";

import { createContext, useContext, type ReactNode } from "react";

/**
 * Provides the name of the "current entity" (shop, project, ...) to the
 * shared `<Breadcrumbs />` rendered in the Header. The Header sits above
 * page content in the tree, so a component further down the tree cannot
 * hand data to the breadcrumbs directly — instead, entity pages wrap
 * their children in this provider and the `useBreadcumbs` hook reads the
 * current entity name from context when present.
 *
 * This avoids the breadcrumbs component re-fetching the entity by id
 * (or, worse on slug-based routes, falling back to a humanised slug
 * segment like "Dm" for "dm").
 */
export type BreadcrumbEntityName = {
    /** Display name used for the last breadcrumb segment on entity pages. */
    name: string;
};

const BreadcrumbEntityContext = createContext<BreadcrumbEntityName | null>(null);

export function BreadcrumbEntityProvider({
    name,
    children,
}: {
    name: string | undefined | null;
    children: ReactNode;
}) {
    // If no name is provided, render a null provider so consumers fall
    // back to the URL-based resolution.
    const value = name ? { name } : null;
    return (
        <BreadcrumbEntityContext.Provider value={value}>
            {children}
        </BreadcrumbEntityContext.Provider>
    );
}

export function useBreadcrumbEntity(): BreadcrumbEntityName | null {
    return useContext(BreadcrumbEntityContext);
}
